import { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";

import { Routes } from "@ctypes";
import { FormColumn } from "@entry/ui";
import { Analytics } from "@services";
import { getRouteForPath } from "@utils";

export const Screen = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding: 40px 0 0;
`;

export const ScreenColumn = styled(FormColumn)`
  flex: 1;

  & > :first-child {
    margin-top: auto;
  }
`;

export const ScreenCta = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-top: auto;
  padding: 28px 0 22px;

  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 50%;
    transform: translateX(-50%);
    width: 100vw;
    z-index: -1;
    background: linear-gradient(
      180deg,
      rgba(9, 11, 31, 0) 0%,
      rgba(9, 11, 31, 0.65) 45%,
      rgba(9, 11, 31, 0.85) 100%
    );
    pointer-events: none;
  }

  & > button {
    max-width: 100%;
  }
`;

/**
 * ScreenCta is sticky with nothing after it, so once stuck it overlaps a
 * constant band the height of the CTA for the rest of the scroll — without
 * a spacer at least that tall reserved above it, the last CTA-height's
 * worth of real content stays hidden underneath it even at max scroll.
 * Attach `ctaRef` to the ScreenCta and render a `{height}` spacer directly
 * above it.
 */
export const useCtaSpacer = () => {
  const ctaRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = ctaRef.current;
    if (!el) {
      setHeight(0);
      return;
    }

    const observer = new ResizeObserver(([entry]) => setHeight(entry.contentRect.height));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ctaRef, ctaSpacerHeight: height };
};

export const useEntryPageAnalytics = (path: Routes) => {
  useEffect(() => {
    Analytics.page(getRouteForPath(path));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
